import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Button, css, Drawer, Layout, MAGNETIC_TOKENS, Overlay, Spinner, toast, utils } from '@appd/uik';
import { ResizableHorizontalGrid } from 'react-resizable-collapsible-grid';
import { DashboardViewer } from '../DashboardViewer';
import { JSONEditor } from '../JSONEditor';
import { DashboardContext, useDashboardJson } from '../../../hooks';
import cartesianWidget from './addWidget/cartesianWidget';
import pieWidget from './addWidget/pieWidget';
import barWidget from './addWidget/barWidget';
import { getTENANTLayerId } from '../../../utils';
import { DashifyEditor } from '../../editor/DashifyEditor';

// todo: clean up
export interface DashboardPageRequest extends Request {
  getTopologyContext(): { filter?: string };
}
export interface DashboardPageProps {
  dashboardName: string;
  request: DashboardPageRequest;
  isEditMode?: boolean;
  useDashifyEditor: boolean;
  useAdvancedDashifyEditor: boolean;
}

const {
  COLORS: { DEFAULT_BG, DEFAULT_TEXT },
  PARTICLE: { COLOR_SCHEME },
  TYPOGRAPHY: { BODY },
} = MAGNETIC_TOKENS;

css.RENDERER.renderStatic(
  {
    // Prevent layout accidents in a dashboard from causing double scrollbars in the outer content.
    contain: 'size',

    // Ensure <Overlay> elements remain correctly positioned while scrolling.
    // <Overlay.Provider> positions itself absolutely. This style ensures the parent is the offset parent.
    position: 'relative',
  },
  'div:has(#fso-dashify)',
);

const instantiateWidget = (count: number, type: string) => {
  let localWidget;
  if (type === 'cartesian') {
    localWidget = { ...cartesianWidget };
  } else if (type === 'pie') {
    localWidget = { ...pieWidget };
  } else if (type === 'bar') {
    localWidget = { ...barWidget };
  } else {
    throw new Error('Invalid widget type');
  }

  localWidget.name = `widget${count}`;
  return localWidget;
};

export const DashboardPage: React.FC<DashboardPageProps> = ({
  dashboardName,
  request,
  isEditMode,
  useDashifyEditor,
  useAdvancedDashifyEditor,
}) => {
  const [epoch, setEpoch] = useState(Date.now());
  const { updateTemplate } = useContext(DashboardContext);
  let template = useDashboardJson({ name: dashboardName });
  const [json, setJSON] = useState(JSON.stringify(template, null, 4));
  const [saveStateMessage, setSaveStateMessage] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const router = useRouter();

  const handleEditorChange = useCallback((value: string) => {
    setJSON(value);
    updateTemplate(JSON.parse(value));
    setEpoch(Date.now());
  }, []);

  // refresh editor when template changes when navigating from 'Clone' button click.
  useEffect(() => {
    setJSON(JSON.stringify(template, null, 4));
  }, [template]);

  const handleAddWidget = useCallback(
    (type: string) => {
      const dashboard = JSON.parse(json);
      if (!dashboard.templates) {
        dashboard.templates = [];
      }
      const currentNumberOfTemplates = dashboard.templates.length;
      const newTemplate = instantiateWidget(currentNumberOfTemplates + 1, type);

      dashboard.templates.push(newTemplate);
      (dashboard.spec?.view?.[0]?.[0] === 'uik:Layout' ? dashboard.spec.view[0][2] : dashboard.spec.view).push([
        'div',
        { style: { height: 300 } },
        [[newTemplate.name]],
      ]);
      handleEditorChange(JSON.stringify(dashboard, null, 4));
    },
    [json, handleEditorChange],
  );

  const { filter } = request?.getTopologyContext() ?? {};

  const saveChanges = useCallback(() => {
    const dashboard = JSON.parse(json);

    // Must happen before `JSON.stringify` below so the new layout is included in the `saveRequest`.
    setLayout(dashboard, handleEditorChange);

    const dashboardId = `${dashboard.name}`;
    const saveRequest = new Request(`/session/api/ext/objstore/v1beta/objects/platformui:dashboard/${dashboardId}`, {
      headers: {
        Accept: 'application/json; charset=UTF-8',
        'layer-type': 'TENANT',
        'layer-id': getTENANTLayerId(),
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(dashboard),
    });

    setShowSpinner(true);
    fetch(saveRequest)
      .then(() => {
        flashMessage('saved!');
      })
      .catch(() => {
        flashMessage('error during saving');
      });
  }, [json, handleEditorChange]);

  const flashMessage = (message: string) => {
    setShowSpinner(false);
    setSaveStateMessage(message);
    setTimeout(() => {
      setSaveStateMessage('');
    }, 3000);
  };

  const exit = () => {
    router.push(`/${router.query.appName}/${dashboardName}`);
  };

  const saveChangesAndExit = () => {
    saveChanges();
    exit();
  };

  if (useAdvancedDashifyEditor) {
    return <DashifyEditor request={request} template={template} />;
  } else if (isEditMode) {
    return (
      <Overlay.Provider>
        <div className="flex flex-col border-2 h-[calc(100vh-56px)] overflow-hidden">
          <div className="p-2 gap-x-2 border-b-2 h-12">
            <div className="flex right-5 gap-x-2.5 ">
              {useDashifyEditor && (
                <>
                  <Button onClick={() => exit()}>Exit</Button>
                  <div className="grow"></div>
                  <div className="self-center">Add Widget:</div>
                  <Button icon="Widget.Graph" onClick={() => handleAddWidget('cartesian')}>
                    Line
                  </Button>
                  <Button icon="Widget.List" onClick={() => handleAddWidget('bar')}>
                    Bar
                  </Button>
                  <Button icon="Widget.Pie" onClick={() => handleAddWidget('pie')}>
                    Pie
                  </Button>
                  <Button type="SECONDARY" icon="Navigation.ChevronDown">
                    More
                  </Button>
                  <div className="grow"></div>
                  {showSpinner && <Spinner size="SMALL" />}
                  <span className="relative top-2">{saveStateMessage}</span>
                  <Button onClick={() => saveChanges()}>Save</Button>
                  <Button onClick={() => saveChangesAndExit()}>Save and Exit</Button>
                </>
              )}
            </div>
          </div>
          <ResizableHorizontalGrid>
            <div className="mr-3">
              <JSONEditor path={dashboardName} initialValue={json} onChange={handleEditorChange} />
            </div>
            <div className="ml-3 overflow-auto">
              <div id="fso-dashify" className={css(STYLES, 'min-h-screen h-max')}>
                <Layout.Provider defaults={{ h: 4, minW: 4, minH: 2 }} editing>
                  <DashboardViewer
                    key={epoch}
                    dashboardName={dashboardName}
                    filter={filter}
                    isEditorMode={true}
                    useDashifyEditor={useDashifyEditor}
                    showPageTitle={true}
                  />
                </Layout.Provider>
              </div>
            </div>
          </ResizableHorizontalGrid>
        </div>
      </Overlay.Provider>
    );
  }

  return (
    <Overlay.Provider>
      <div id="fso-dashify" className={css(STYLES, 'min-h-screen h-max')}>
        <DashboardViewer
          dashboardName={dashboardName}
          filter={filter}
          isEditorMode={false}
          useDashifyEditor={useDashifyEditor}
          useAdvancedDashifyEditor={useAdvancedDashifyEditor}
          showPageTitle={true}
        />
      </div>
      <Drawer.Portal />
    </Overlay.Provider>
  );
};

const STYLES = css({
  ...BODY.DEFAULT,
  backgroundColor: DEFAULT_BG,
  color: DEFAULT_TEXT,
  colorScheme: COLOR_SCHEME,
  height: '100%',
});

/** Writes latest `Layout.snapshot()` to `dashboard` and notifies JSON editor. */
const setLayout = (dashboard: any, handleEditorChange: (value: string) => void) => {
  try {
    if (!Array.isArray(dashboard.spec?.view)) {
      return;
    }

    const layoutProvider = dashboard.spec.view.find(
      (element: any) =>
        utils.maybeGet(element, 0) === 'uik:Layout' || utils.maybeGet(element, 'instanceOf') === 'uik:Layout',
    );

    if (layoutProvider) {
      (Array.isArray(layoutProvider) ? (layoutProvider[1] ??= {}) : layoutProvider).saved = `\${ ${JSON.stringify(
        Layout.snapshot(),
      ).replaceAll('"', `'`)} }`;

      handleEditorChange(JSON.stringify(dashboard, null, 4));

      toast({
        icon: 'SUCCESS',
        title: 'Layout written to JSON under "saved".',
      });
    }
  } catch {}
};
